/**
 * UndoRedoControls Component
 *
 * UI controls for undo/redo functionality:
 * - Undo button with Ctrl/Cmd+Z shortcut
 * - Redo button with Ctrl/Cmd+Shift+Z shortcut
 * - Visual indicator showing available actions
 * - Disabled state when stacks are empty
 */

import { useEffect } from 'react';
import { Undo2, Redo2 } from 'lucide-react';
import { useSceneStore } from '../../stores/sceneStore';
import { useHistoryStore } from '../../stores/historyStore';
import { useTimelineStore } from '../../stores/timelineStore';

export function UndoRedoControls() {
  const activeTabId = useSceneStore((state) => state.activeTabId);
  const undo = useHistoryStore((state) => state.undo);
  const redo = useHistoryStore((state) => state.redo);
  const getHistoryForTab = useHistoryStore((state) => state.getHistoryForTab);

  const history = activeTabId ? getHistoryForTab(activeTabId) : null;
  const undoCount = history?.undoStack.length || 0;
  const redoCount = history?.redoStack.length || 0;

  const canUndo = undoCount > 0;
  const canRedo = redoCount > 0;

  const handleUndo = () => {
    if (!activeTabId || !canUndo) return;

    const previousSnapshot = undo(activeTabId);
    if (previousSnapshot) {
      const snap = previousSnapshot.timelineSnapshot;
      useTimelineStore.setState({
        tools: snap.tools as any[],
        tracks: snap.tracks as any[],
        groups: (snap.groups ?? []) as any[],
        placedGroups: (snap.placedGroups ?? []) as any[],
        duration: snap.duration,
        playheadPosition: snap.playheadPosition,
      });
    } else {
      useTimelineStore.setState({
        tools: [],
        tracks: [
          { number: 1, label: 'Track 1' },
          { number: 2, label: 'Track 2' },
          { number: 3, label: 'Track 3' },
        ],
        groups: [],
        placedGroups: [],
        duration: 10,
        playheadPosition: 0,
      });
    }
  };

  const handleRedo = () => {
    if (!activeTabId || !canRedo) return;

    const snapshotToRestore = redo(activeTabId);
    if (snapshotToRestore) {
      const snap = snapshotToRestore.timelineSnapshot;
      useTimelineStore.setState({
        tools: snap.tools as any[],
        tracks: snap.tracks as any[],
        groups: (snap.groups ?? []) as any[],
        placedGroups: (snap.placedGroups ?? []) as any[],
        duration: snap.duration,
        playheadPosition: snap.playheadPosition,
      });
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Ctrl/Cmd+Z (Undo)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      // Check for Ctrl/Cmd+Shift+Z (Redo)
      else if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z') {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTabId, canUndo, canRedo]);

  return (
    <div className="flex items-center gap-2">
      {/* Undo Button */}
      <button
        onClick={handleUndo}
        disabled={!canUndo}
        className="flex items-center gap-1.5 rounded bg-neutral-800 px-3 py-1.5 text-sm text-neutral-200 transition-colors hover:bg-neutral-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-neutral-800"
        aria-label="Undo"
        title={`Undo (Ctrl/Cmd+Z)${canUndo ? ` - ${undoCount} action${undoCount !== 1 ? 's' : ''} available` : ''}`}
      >
        <Undo2 className="h-4 w-4" />
        <span className="hidden sm:inline">Undo</span>
      </button>

      {/* Redo Button */}
      <button
        onClick={handleRedo}
        disabled={!canRedo}
        className="flex items-center gap-1.5 rounded bg-neutral-800 px-3 py-1.5 text-sm text-neutral-200 transition-colors hover:bg-neutral-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-neutral-800"
        aria-label="Redo"
        title={`Redo (Ctrl/Cmd+Shift+Z)${canRedo ? ` - ${redoCount} action${redoCount !== 1 ? 's' : ''} available` : ''}`}
      >
        <Redo2 className="h-4 w-4" />
        <span className="hidden sm:inline">Redo</span>
      </button>

      {/* Action count indicator */}
      {(canUndo || canRedo) && (
        <span className="hidden text-xs text-neutral-500 md:inline">
          {undoCount > 0 && `${undoCount} undo`}
          {undoCount > 0 && redoCount > 0 && ' / '}
          {redoCount > 0 && `${redoCount} redo`}
        </span>
      )}
    </div>
  );
}
